import React from 'react';
type Group = {
  name: string;
  color: string;
  cx: number;
  /** Where the group works on the field. */
  gy: number;
  supervised: boolean;
};
const GROUPS: Group[] = [{
  name: 'SPRINTS',
  color: '#C8452B',
  cx: 36,
  gy: 122,
  supervised: false
}, {
  name: 'HURDLES',
  color: '#C8452B',
  cx: 98,
  gy: 166,
  supervised: true
}, {
  name: 'JUMPS',
  color: '#E89B0C',
  cx: 160,
  gy: 122,
  supervised: false
}, {
  name: 'THROWS',
  color: '#2E7D4F',
  cx: 222,
  gy: 166,
  supervised: true
}, {
  name: 'DISTANCE',
  color: '#2563A8',
  cx: 284,
  gy: 122,
  supervised: false
}];
const COACH: [number, number] = [232, 214];
const NAVY = '#14213D';
const OSWALD = "Oswald, 'Arial Narrow', sans-serif";

/** Athlete dots around a group centre. */
const DOTS: [number, number][] = [[-11, -4], [0, -10], [11, -4], [-6, 8], [6, 8]];
function Eye({
  x,
  y
}: {
  x: number;
  y: number;
}) {
  return <g transform={`translate(${x} ${y})`} data-id="element-129" data-mp="335d9n1x5z1f7">
      <circle r="9" fill="#FFFFFF" stroke={NAVY} strokeWidth="1.4" data-id="element-130" data-mp="33428zaz0m9sb" />
      <path d="M-5.6 0 Q0 -5.2 5.6 0 Q0 5.2 -5.6 0 Z" fill="none" stroke={NAVY} strokeWidth="1.3" data-id="element-131" data-mp="1xs7me821blk2" />
      <circle r="1.9" fill={NAVY} data-id="element-132" data-mp="1su63po8ito94" />
    </g>;
}

/** The fence, the five cards, the groups and the coach. Also used on the Volume 1 sample page. */
export function FenceDiagram({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 320 240" className={className} role="img" aria-label="Five practice cards posted on the fence, one each for sprints, hurdles, jumps, throws and distance. Sprints, jumps drills and distance work from their card. The coach stands with the throws group and keeps every other group in sight. Hurdles and throws are coach-supervised only." data-id="element-133" data-mp="22kqz72swecnt">
    {/* the fence */}
    <path d="M6 16 H314" stroke={NAVY} strokeWidth="2.2" data-id="element-134" data-mp="1iup1jgsh9shw" />
    {[6, 67, 129, 191, 253, 314].map(x => <path key={x} d={`M${x} 9 V24`} stroke={NAVY} strokeWidth="2.2" strokeLinecap="round" data-id="element-135" data-mp="2z85datb2xxio" />)}
    <path d={Array.from({
      length: 38
    }, (_, i) => `M${8 + i * 8} 18 l4 6 l4 -6`).join(' ')} stroke={NAVY} strokeOpacity=".18" strokeWidth=".8" fill="none" data-id="element-136" data-mp="2ua3um9hkg07q" />

    {/* sight lines from the coach */}
    {GROUPS.filter(g => g.name !== 'THROWS').map(g => <path key={g.name} d={`M${COACH[0]} ${COACH[1]} L${g.cx} ${g.gy}`} stroke={NAVY} strokeOpacity=".45" strokeWidth="1.2" strokeDasharray="3 4" data-id="element-137" data-mp="20z73tzlcykhr" />)}

    {GROUPS.map(g => <g key={g.name} data-id="element-138" data-mp="194wqxxe3wiq7">
        {/* the card, hanging from the fence */}
        <g transform={`translate(${g.cx - 27} 20)`} data-id="element-139" data-mp="1d8kyig2qnebg">
          <rect x="0" y="0" width="54" height="44" rx="4" fill="#FFFFFF" stroke="#E4DCCB" strokeWidth="1" data-id="element-140" data-mp="1i6w76nharis3" />
          <rect x="0" y="0" width="54" height="12" rx="4" fill={g.color} data-id="element-141" data-mp="1mye85bjbljd2" />
          <rect x="0" y="8" width="54" height="4" fill={g.color} data-id="element-142" data-mp="pj0edtuumb0l" />
          <circle cx="27" cy="4" r="1.6" fill="#FFFFFF" data-id="element-143" data-mp="pigyeiordwp7" />
          <text x="27" y="23" textAnchor="middle" fill={NAVY} fontFamily={OSWALD} fontWeight="700" fontSize="8.4" letterSpacing=".3" data-id="element-144" data-mp="uh1wabgjg4ah">
            {g.name}
          </text>
          <path d="M8 30 H46 M8 35 H40 M8 40 H44" stroke={NAVY} strokeOpacity=".3" strokeWidth="1.4" strokeLinecap="round" data-id="element-145" data-mp="hzhd27hqlv37" />
        </g>

        {/* the group on the field */}
        {g.supervised && <circle cx={g.cx} cy={g.gy} r="21" fill="none" stroke={g.color} strokeWidth="1.4" strokeDasharray="3 3" data-id="element-146" data-mp="8yt8e3e8h72z" />}
        {DOTS.map(([dx, dy], i) => <circle key={i} cx={g.cx + dx} cy={g.gy + dy} r="4.6" fill={g.color} stroke="#FFFFFF" strokeWidth="1.4" data-id="element-147" data-mp="41jcc4wc9x66" data-mpi={i} />)}
        {g.supervised && <Eye x={g.cx + 21} y={g.gy - 19} data-id="element-148" data-mp="3qehwye6tpxfu" />}
      </g>)}

    {/* the coach */}
    <circle cx={COACH[0]} cy={COACH[1]} r="13" fill="#F2C230" stroke={NAVY} strokeWidth="2" data-id="element-149" data-mp="3lfy55x5jtrfk" />
    <text x={COACH[0]} y={COACH[1] + 5} textAnchor="middle" fill={NAVY} fontFamily={OSWALD} fontWeight="700" fontSize="14" data-id="element-150" data-mp="3hecywunisbec">
      C
    </text>
    <text x={COACH[0] - 20} y={COACH[1] + 4} textAnchor="end" fill={NAVY} fontFamily="Inter, sans-serif" fontWeight="700" fontSize="9" data-id="element-151" data-mp="3cgb6hj47ezq7">
      you, with the throwers
    </text>
  </svg>;
}

/**
 * Page 6 of Volume 1, as a diagram: one Fence Card per group posted on the
 * fence, the coach standing with the supervised group and sight lines to
 * every other group.
 */
export function FenceLine() {
  return <div className="flex h-full flex-col rounded-3xl bg-chalk-light p-5 text-left text-ink shadow-page sm:p-7" data-id="element-152" data-mp="1bju3atxik74u">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-clay" data-id="element-153" data-mp="wmtb6pbeua5u">Space</p>
      <h3 className="font-display text-xl font-bold uppercase leading-tight sm:text-2xl" data-id="element-154" data-mp="11osbtrfrmsz3">The Fence Line</h3>
      <p className="mt-1 text-[13px] leading-snug text-ink-soft" data-id="element-155" data-mp="npajx376azli">
        Five groups, five cards on the fence. The card does the talking so you can do the coaching.
      </p>

      <FenceDiagram className="mx-auto mt-4 w-full max-w-[360px]" data-id="element-156" data-mp="2i2sa6xyyqak8" />

      <ul className="mt-auto flex flex-wrap gap-x-4 gap-y-1.5 border-t border-chalk-dark pt-3 text-[12px] text-ink-soft" data-id="element-157" data-mp="2n13k18p9cl2g">
        <li className="flex items-center gap-1.5" data-id="element-158" data-mp="2x5jx5ubcro2k">
          <span className="h-2.5 w-2.5 rounded-full bg-lane" data-id="element-159" data-mp="2s5e54d167zjy" />
          Self-run from the card
        </li>
        <li className="flex items-center gap-1.5" data-id="element-160" data-mp="35wkagpr4srer">
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-ink text-[8px] leading-none" data-id="element-161" data-mp="35ydh451qvbmr">◉</span>
          Coach-supervised only
        </li>
        <li className="flex items-center gap-1.5" data-id="element-162" data-mp="3ftxdsto4ueck">
          <span className="h-2.5 w-2.5 rounded-full border border-ink bg-hl" data-id="element-163" data-mp="3fwt7u6wv0ivq" />
          Coach
        </li>
      </ul>
    </div>;
}